import { useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { useAppSelector } from 'src/store';
import {
  useGetProjectByIdQuery,
  useGetZendeskEmailGroupsFieldsQuery,
  useZendeskEmailIntegrationAutoSetupMutation,
} from 'src/services';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { Skeleton } from 'src/components/ui/skeleton';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { ZendeskEscalationGroupSelect } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/ZendeskEscalationGroupSelect/ZendeskEscalationGroupSelect';

const InitialGroupsFields = [{ id: null, name: 'Don’t change group' }];

export const ZendeskEmailStep4 = ({
  onClose = (_v?: any) => {},
  onClick = (_d?: any) => {},
}: {
  onClose?: (v?: any) => void;
  onClick?: (d?: any) => void;
}) => {
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const [groupsFields, setGroupsFields] = useState(InitialGroupsFields);
  const [page, setPage] = useState(1);
  const { data: currentProject } = useGetProjectByIdQuery({ id: id }) as any;
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const {
    data: groupsFieldsResponse,
    isLoading: isLoadingGroupsFields,
    isFetching: isFetchingGroupsFields,
    isError: isErrorGroupsFields,
  } = useGetZendeskEmailGroupsFieldsQuery(
    {
      projectId: id,
      integrationId: selectedIntegration?.id,
      page: page,
    },
    { skip: !selectedIntegration || !selectedIntegration.id || !currentProject },
  ) as any;
  const [
    setZendeskEmailAutoSetup,
    {
      isSuccess: isSuccessSetZendeskEmailAutoSetup,
      isLoading: isLoadingSetZendeskEmailAutoSetup,
      isError: isErrorSetZendeskEmailAutoSetup,
    },
  ] = useZendeskEmailIntegrationAutoSetupMutation();
  const dispatch = useDispatch();

  const { handleSubmit, control } = useForm();

  useEffect(() => {
    if (isLoadingGroupsFields || isLoadingSetZendeskEmailAutoSetup) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingGroupsFields, isLoadingSetZendeskEmailAutoSetup]);

  useEffect(() => {
    if (groupsFieldsResponse) {
      setGroupsFields([...groupsFields, ...groupsFieldsResponse.items]);

      if (groupsFieldsResponse.has_more) {
        setPage(page + 1);
      }
    }
  }, [groupsFieldsResponse]);

  useEffect(() => {
    if (isSuccessSetZendeskEmailAutoSetup) {
      onClick({ ...selectedIntegration });
    }
  }, [isSuccessSetZendeskEmailAutoSetup]);

  const submit = async (data) => {
    setZendeskEmailAutoSetup({
      projectId: id,
      integrationId: selectedIntegration.id,
      body: {
        escalated_group_id: data.escalated_group_id || '',
      },
    });
  };

  return (
    <form id="zendesk-email-step4-form" onSubmit={handleSubmit(submit)}>
      <div className="content">
        <LoaderWrapper
          loader={<Skeleton className="w-full h-16" />}
          isLoading={isFetchingGroupsFields}
        >
          {!isErrorGroupsFields ? (
            groupsFields.length > 1 && (
              <div className={'flex flex-col gap-5'}>
                <ZendeskEscalationGroupSelect
                  label={
                    <div className={'flex flex-row gap-2 items-center mb-1'}>
                      Escalation group{' '}
                      <InputInfoToolTip
                        infoToolTip={{
                          side: 'right',
                          description: <>AI will assign escalated tickets to this group.</>,
                        }}
                      />
                    </div>
                  }
                  control={control}
                  name={'escalated_group_id'}
                  options={groupsFields}
                />
              </div>
            )
          ) : (
            <p>
              The Zendesk details you entered seem invalid. Please double-check your subdomain,
              email, and API token following{' '}
              <a href={selectedIntegration.setupGuideUrl} target="_blank" rel="noreferrer">
                this guide
              </a>
              .
            </p>
          )}
        </LoaderWrapper>
      </div>
    </form>
  );
};
